import React from 'react'
import { BrowserRouter, Route, Switch, withRouter } from 'react-router-dom'

import ApplicationScreenWrapper from './application/ApplicationScreenWrapper'
import LoadingScreen from './application/loading/LoadingScreen'
import { useApplicationVariantKey } from './application/hooks/useApplicationVariants'
import { RouterContext } from './contexts'
import { withTracker } from './ga'
import { origins } from './constants'

// Player
const LazyPlayerContainer = React.lazy(() =>
  import('./application/player/PlayerContainer'),
)

// Greetings
const LazyGreetingsContainer = React.lazy(() =>
  import('./application/greetings/GreetingsContainer'),
)

// Partner
const LazyPartnerGreetingsContainer = React.lazy(() =>
  import('./application/variants/partner/greetings/GreetingsContainer'),
)
const LazyPartnerRegistrationContainer = React.lazy(() =>
  import('./application/variants/partner/registration/RegistrationContainer'),
)

// Mentor
const LazyMentorGreetingsContainer = React.lazy(() =>
  import('./application/variants/mentor/greetings/GreetingsContainer'),
)
const LazyMentorRegistrationContainer = React.lazy(() =>
  import('./application/variants/mentor/registration/RegistrationContainer'),
)

// Judge
const LazyJudgeGreetingsContainer = React.lazy(() =>
  import('./application/variants/judge/greetings/GreetingsContainer'),
)
const LazyJudgeRegistrationContainer = React.lazy(() =>
  import('./application/variants/judge/registration/RegistrationContainer'),
)

// Judge Step 2
const LazyJudgeStep2GreetingsContainer = React.lazy(() =>
  import('./application/variants/judgestep2/greetings/GreetingsContainer'),
)
const LazyJudgeStep2RegistrationContainer = React.lazy(() =>
  import(
    './application/variants/judgestep2/registration/RegistrationContainer'
  ),
)

// Individual
// const LazyIndividualGreetingsContainer = React.lazy(() =>
//   import('./application/variants/individual/greetings/GreetingsContainer'),
// )
const LazyIndividualRegistrationContainer = React.lazy(() =>
  import(
    './application/variants/individual/registration/RegistrationContainer'
  ),
)

// Startup
const LazyStartupRegistrationContainer = React.lazy(() =>
  import('./application/variants/startups/registration/RegistrationContainer'),
)

// Reports
const LazyReportsGreetingsContainer = React.lazy(() =>
  import('./application/variants/reports/greetings/GreetingsContainer'),
)
const LazyReportARegistrationContainer = React.lazy(() =>
  import('./application/variants/reporta/registration/RegistrationContainer'),
)
const LazyReportBRegistrationContainer = React.lazy(() =>
  import('./application/variants/reportb/registration/RegistrationContainer'),
)
const LazyReportCRegistrationContainer = React.lazy(() =>
  import('./application/variants/reportc/registration/RegistrationContainer'),
)

// Voting
const LazyVotingRoute = React.lazy(() =>
  import('./application/voting/VotingRoute'),
)

// display
const LazyDisplayRoute = React.lazy(() =>
  import('./application/display/VotingRoute'),
)

const ApplicationRouterContextProvider = withRouter(
  ({ children, ...routerProps }) => (
    <RouterContext.Provider value={routerProps}>
      {children}
    </RouterContext.Provider>
  ),
)

function onRouterUpdate() {
  /* global document */
  const main = document.querySelector('main')
  if (main && typeof main.scrollTo === 'function') {
    main.scrollTo(0, 0)
  }
}

export default function ApplicationRouter() {
  const individualOriginIdentifier = useApplicationVariantKey(
    origins.individualOriginIdentifier,
    'isRegistrationOpen',
  )

  const judgeOriginIdentifier = useApplicationVariantKey(
    origins.judgeOriginIdentifier,
    'isRegistrationOpen',
  )

  const judgeStep2OriginIdentifier = useApplicationVariantKey(
    origins.judgeStep2OriginIdentifier,
    'isRegistrationOpen',
  )

  const mentorOriginIdentifier = useApplicationVariantKey(
    origins.mentorOriginIdentifier,
    'isRegistrationOpen',
  )

  const partnerOriginIdentifier = useApplicationVariantKey(
    origins.partnerOriginIdentifier,
    'isRegistrationOpen',
  )
  const startupsOriginIdentifier = useApplicationVariantKey(
    origins.startupsOriginIdentifier,
    'isRegistrationOpen',
  )

  const reportAOriginIdentifier = useApplicationVariantKey(
    origins.reportAOriginIdentifier,
    'isRegistrationOpen',
  )

  const reportBOriginIdentifier = useApplicationVariantKey(
    origins.reportBOriginIdentifier,
    'isRegistrationOpen',
  )

  const reportCOriginIdentifier = useApplicationVariantKey(
    origins.reportCOriginIdentifier,
    'isRegistrationOpen',
  )

  return (
    <BrowserRouter onUpdate={onRouterUpdate}>
      <ApplicationRouterContextProvider>
        <React.Suspense fallback={<LoadingScreen />}>
          <ApplicationScreenWrapper>
            <Switch>
              <Route
                component={withTracker(LazyPlayerContainer)}
                path="/player/:id"
              />

              {individualOriginIdentifier && (
                <Route path="/participante">
                  <Switch>
                    <Route
                      exact
                      path="/participante/cadastro"
                      component={withTracker(
                        LazyIndividualRegistrationContainer,
                      )}
                    />
                  </Switch>
                </Route>
              )}

              {mentorOriginIdentifier && (
                <Route path="/mentor">
                  <Switch>
                    <Route
                      exact
                      path="/mentor/cadastro"
                      component={withTracker(LazyMentorRegistrationContainer)}
                    />

                    <Route
                      component={withTracker(LazyMentorGreetingsContainer)}
                    />
                  </Switch>
                </Route>
              )}

              {judgeOriginIdentifier && (
                <Route path="/jurado">
                  <Switch>
                    <Route
                      exact
                      path="/jurado/cadastro"
                      component={withTracker(LazyJudgeRegistrationContainer)}
                    />

                    <Route
                      component={withTracker(LazyJudgeGreetingsContainer)}
                    />
                  </Switch>
                </Route>
              )}

              {judgeStep2OriginIdentifier && (
                <Route path="/banca">
                  <Switch>
                    <Route
                      exact
                      path="/banca/cadastro"
                      component={withTracker(
                        LazyJudgeStep2RegistrationContainer,
                      )}
                    />

                    <Route
                      component={withTracker(LazyJudgeStep2GreetingsContainer)}
                    />
                  </Switch>
                </Route>
              )}

              {partnerOriginIdentifier && (
                <Route path="/parceiros">
                  <Switch>
                    <Route
                      exact
                      path="/parceiros/cadastro"
                      component={withTracker(LazyPartnerRegistrationContainer)}
                    />

                    <Route
                      component={withTracker(LazyPartnerGreetingsContainer)}
                    />
                  </Switch>
                </Route>
              )}

              {startupsOriginIdentifier && (
                <Route path="/startups">
                  <Switch>
                    <Route
                      exact
                      path="/startups/cadastro"
                      component={withTracker(LazyStartupRegistrationContainer)}
                    />
                  </Switch>
                </Route>
              )}

              <Route path="/entregas">
                <Switch>
                  {reportAOriginIdentifier && (
                    <Route
                      exact
                      path="/entregas/entrega-1"
                      component={withTracker(LazyReportARegistrationContainer)}
                    />
                  )}

                  {reportBOriginIdentifier && (
                    <Route
                      exact
                      path="/entregas/entrega-2"
                      component={withTracker(LazyReportBRegistrationContainer)}
                    />
                  )}

                  {reportCOriginIdentifier && (
                    <Route
                      exact
                      path="/entregas/entrega-3"
                      component={withTracker(LazyReportCRegistrationContainer)}
                    />
                  )}

                  <Route
                    component={withTracker(LazyReportsGreetingsContainer)}
                  />
                </Switch>
              </Route>

              <Route
                path="/votacao/:team"
                component={withTracker(LazyVotingRoute)}
              />

              <Route
                path="/equipe/:team"
                component={withTracker(LazyDisplayRoute)}
              />

              <Route
                exact
                path="/politicas-de-privacidade"
                component={() => {
                  // eslint-disable-next-line no-undef
                  window.location.href = 'https://paniclobster.com/pt/privacy'
                  return null
                }}
              />

              <Route
                exact
                path="/regulamento"
                component={() => {
                  // eslint-disable-next-line no-undef
                  window.location.href =
                    'https://cdn.paniclobster.com/hackathons/porto-do-acu/regulamento.pdf'
                  return null
                }}
              />

              <Route
                exact
                path="/termos-de-uso"
                component={() => {
                  // eslint-disable-next-line no-undef
                  window.location.href = 'https://paniclobster.com/pt/terms/'
                  return null
                }}
              />

              <Route component={withTracker(LazyGreetingsContainer)} />
            </Switch>
          </ApplicationScreenWrapper>
        </React.Suspense>
      </ApplicationRouterContextProvider>
    </BrowserRouter>
  )
}
